// Mobile optimization utilities
export const isMobileDevice = () => {
  return typeof window !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768
  );
};

export const addMobileClass = () => {
  if (typeof window !== 'undefined') {
    const isMobile = isMobileDevice();
    const body = document.body;
    
    if (isMobile) {
      body.classList.add('mobile-device');
      body.classList.add('touch-device');
    } else {
      body.classList.remove('mobile-device');
      body.classList.remove('touch-device');
    }
  }
};

// Completely disable section interactions on mobile
export const disableSectionInteractions = () => {
  if (typeof window !== 'undefined' && isMobileDevice()) {
    // Remove all event listeners from non-functional sections
    const sections = document.querySelectorAll('section, article, div:not([onclick]):not([role="button"]), header, main, footer, aside');
    
    sections.forEach(section => {
      // Clone and replace element to remove all event listeners
      if (!section.matches('nav, nav *, .navbar, .navbar *, [class*="Nav"], [class*="Nav"] *, [class*="Button"], [class*="button"], [class*="Link"], [class*="link"], [class*="Modal"], [class*="modal"], a, button, input, textarea, select, [onclick], [href], [role="button"], [tabindex]')) {
        
        // Disable pointer events via style
        section.style.setProperty('pointer-events', 'none', 'important');
        section.style.setProperty('cursor', 'default', 'important');
        section.style.setProperty('-webkit-tap-highlight-color', 'transparent', 'important');
        section.style.setProperty('-webkit-touch-callout', 'none', 'important');
        section.style.setProperty('-webkit-user-select', 'none', 'important');
        section.style.setProperty('user-select', 'none', 'important');
      }
    });
    
    // Re-enable interactions for truly functional elements
    const functionalElements = document.querySelectorAll('a, button, input, textarea, select, [onclick], [href], [role="button"], [tabindex]:not([tabindex="-1"]), nav, nav *, .navbar, .navbar *, [class*="Nav"], [class*="Nav"] *, [class*="Button"], [class*="button"], [class*="Link"], [class*="link"], [class*="Modal"], [class*="modal"], [class*="ServiceCard"], [class*="FieldCard"]');
    
    functionalElements.forEach(element => {
      element.style.setProperty('pointer-events', 'auto', 'important');
    });
    
    // Add touch event prevention for sections on mobile
    document.addEventListener('touchstart', (e) => {
      if (e.target.matches('section, article, div:not([onclick]):not([role="button"]), header, main, footer, aside') &&
          !e.target.matches('nav, nav *, .navbar, .navbar *, [class*="Nav"], [class*="Nav"] *, [class*="Button"], [class*="button"], [class*="Link"], [class*="link"], [class*="Modal"], [class*="modal"], a, button, input, textarea, select, [onclick], [href], [role="button"], [tabindex]')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { passive: false });
    
    // Add click event prevention for sections on mobile
    document.addEventListener('click', (e) => {
      if (e.target.matches('section, article, div:not([onclick]):not([role="button"]), header, main, footer, aside') &&
          !e.target.matches('nav, nav *, .navbar, .navbar *, [class*="Nav"], [class*="Nav"] *, [class*="Button"], [class*="button"], [class*="Link"], [class*="link"], [class*="Modal"], [class*="modal"], a, button, input, textarea, select, [onclick], [href], [role="button"], [tabindex]')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }
};

export const optimizeForMobile = () => {
  if (typeof window !== 'undefined') {
    // Add mobile classes
    addMobileClass();
    
    // Disable card overlay effects
    disableCardEffects();
    
    // Disable section interactions
    disableSectionInteractions();
    
    // Prevent modal background issues
    preventModalBackgroundIssues();
    
    // Disable smooth scrolling on mobile for better performance
    if (isMobileDevice()) {
      document.documentElement.style.scrollBehavior = 'auto';
    }
    
    // Add window resize listener
    window.addEventListener('resize', () => {
      addMobileClass();
      disableCardEffects();
      disableSectionInteractions();
    });
    
    // Add touch event optimizations
    if ('ontouchstart' in window) {
      document.body.classList.add('touch-device');
    }
  }
};

// Touch feedback for mobile buttons
export const addTouchFeedback = (element) => {
  if (!element || !isMobileDevice()) return;
  
  element.addEventListener('touchstart', () => {
    element.style.backgroundColor = 'rgba(7, 148, 135, 0.1)';
    element.style.transform = 'scale(0.98)';
  });
  
  element.addEventListener('touchend', () => {
    setTimeout(() => {
      element.style.backgroundColor = '';
      element.style.transform = '';
    }, 150);
  });
};

// Completely disable card hover effects on touch devices
export const disableCardEffects = () => {
  if (typeof window !== 'undefined' && isMobileDevice()) {
    // Remove existing style if present
    const existingStyle = document.getElementById('nuclear-card-override');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Add nuclear CSS to completely disable card overlays
    const style = document.createElement('style');
    style.id = 'nuclear-card-override';
    style.textContent = `
      @media (max-width: 768px) {
        /* NUCLEAR OPTION: Force disable ALL card effects and styled-components */
        [class*="Card"], [class*="card"],
        div[class^="sc-"], div[class*="sc-"],
        [data-styled], [class*="ServiceCard"], [class*="FieldCard"] {
          background: white !important;
          background-color: white !important;
          background-image: none !important;
          animation: none !important;
          transition: none !important;
          transform: none !important;
          mix-blend-mode: normal !important;
          isolation: auto !important;
          filter: none !important;
          backdrop-filter: none !important;
          pointer-events: auto !important;
        }
        
        /* Force disable ALL pseudo-elements universally */
        *::before, *::after {
          content: none !important;
          display: none !important;
          opacity: 0 !important;
          background: none !important;
          background-image: none !important;
          background-color: transparent !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        
        /* Force disable ALL interactive states */
        [class*="Card"]:hover, [class*="card"]:hover,
        [class*="Card"]:active, [class*="card"]:active,
        [class*="Card"]:focus, [class*="card"]:focus,
        div[class^="sc-"]:hover, div[class*="sc-"]:hover,
        div[class^="sc-"]:active, div[class*="sc-"]:active,
        div[class^="sc-"]:focus, div[class*="sc-"]:focus,
        *:hover, *:active, *:focus, *:touch {
          background: white !important;
          background-color: white !important;
          background-image: none !important;
          animation: none !important;
          transition: none !important;
          transform: none !important;
          mix-blend-mode: normal !important;
          isolation: auto !important;
          filter: none !important;
          backdrop-filter: none !important;
        }
        
        /* Prevent modal background bleed-through */
        [class*="Modal"],
        [class*="modal"] {
          z-index: 9999 !important;
          background: rgba(0, 0, 0, 0.8) !important;
          backdrop-filter: none !important;
        }
        
        /* Isolate modal content */
        [class*="ModalContent"],
        [class*="modalContent"] {
          background: white !important;
          background-color: white !important;
          background-image: none !important;
          isolation: isolate !important;
        }
        
        /* Prevent body background changes when modal is open */
        body.modal-open,
        body[style*="overflow"] {
          background: #f5f7fa !important;
          background-color: #f5f7fa !important;
          background-image: none !important;
        }
        
        /* Prevent header/section background changes */
        header, section, main, div[class*="Wrapper"] {
          background-attachment: scroll !important;
        }
        
        header:hover, section:hover, main:hover, div[class*="Wrapper"]:hover {
          background: inherit !important;
          background-color: inherit !important;
          background-image: inherit !important;
          opacity: 1 !important;
          filter: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Add global touch event prevention
    document.addEventListener('touchstart', function(e) {
      const target = e.target;
      if (target.closest('[class*="Card"]') || target.closest('[class*="card"]')) {
        // Prevent any background changes
        e.preventDefault();
        e.stopPropagation();
      }
    }, { passive: false });
  }
};

// Prevent zoom on iOS double tap
export const preventZoom = () => {
  if (typeof window !== 'undefined') {
    let lastTouchEnd = 0;
    document.addEventListener('touchend', function (event) {
      const now = (new Date()).getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
  }
};

// Prevent modal background interference
export const preventModalBackgroundIssues = () => {
  if (typeof window !== 'undefined' && isMobileDevice()) {
    // Observe for modal opens
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          // Check for modal elements
          const modals = document.querySelectorAll('[class*="Modal"], [class*="modal"]');
          modals.forEach(modal => {
            if (modal.style.display !== 'none' && modal.style.visibility !== 'hidden') {
              // Force stable body background when modal is open
              document.body.style.background = '#f5f7fa';
              document.body.style.backgroundColor = '#f5f7fa';
              document.body.style.backgroundImage = 'none';
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }
};
